/* eslint-disable react/jsx-no-bind */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  InfoOutlined,
  LinkOutlined,
  Warning,
} from '@mui/icons-material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  ALERT_APPEARANCES,
  ALERT_ICONS,
  createEmptyAlert,
} from '../Alert.model';
import { CTA_TEXT_MAX_LENGTH, validateAlert } from '../Alert.validation';
import AlertPreview from '../AlertPreview';
import TargetingForm from '../../shared/components/TargetingForm';
import colors from '../../colors.styles';
import config from '../../config';
import adminFormStyles from '../../shared/styles/adminForm.styles';

const { ALL_LANGUAGES, DEFAULT_LANGUAGE } = config;

const LANGUAGE_OPTIONS = {
  en: { code: 'EN', label: 'English', name: 'English' },
  es: { code: 'ES', label: 'Español', name: 'Spanish' },
  fr: { code: 'FR', label: 'Français', name: 'French' },
};

function languageOption(langKey) {
  return (
    LANGUAGE_OPTIONS[langKey] || {
      code: langKey.toUpperCase(),
      label: langKey,
      name: langKey,
    }
  );
}

const languageVersionCopy =
  ALL_LANGUAGES.length > 1
    ? ` Complete versions for ${ALL_LANGUAGES.map(
        (langKey) => languageOption(langKey).name
      ).join(' and ')}.`
    : '';

const LINK_HELP = (
  <>
    In-app links: CreatePost, Priorities, Leaderboard.
    <br />
    External links: include the full address, starting with https://
  </>
);

const styles = {
  ...adminFormStyles,
  backIcon: {
    fontSize: '14px !important',
  },
  languageSelector: {
    backgroundColor: colors.base.off_white,
    border: `1px solid ${colors.base.blue_gray}`,
    borderRadius: '12px',
    display: 'flex',
    overflow: 'hidden',
    width: '100%',
  },
  languageOption: (isSelected) => ({
    alignItems: 'center',
    backgroundColor: isSelected ? colors.base.light : colors.base.off_white,
    border: '2px solid',
    borderColor: isSelected ? colors.base.walmart_blue : 'transparent',
    borderRadius: '12px',
    color: isSelected ? colors.base.bentonville_blue : colors.base.slate_gray,
    display: 'flex',
    flex: '1 1 0',
    gap: '12px',
    justifyContent: 'flex-start',
    minWidth: 0,
    p: '12px',
    '&:hover': {
      backgroundColor: isSelected ? colors.base.light : colors.base.off_white,
    },
  }),
  languageBadge: (isSelected) => ({
    alignItems: 'center',
    backgroundColor: isSelected
      ? colors.base.walmart_blue
      : colors.base.blue_gray,
    borderRadius: '8px',
    color: isSelected ? colors.base.light : colors.base.slate_gray,
    display: 'flex',
    flexShrink: 0,
    fontSize: '10px',
    fontWeight: 700,
    height: 28,
    justifyContent: 'center',
    lineHeight: 'normal',
    width: 28,
  }),
  languageName: {
    flex: 1,
    fontSize: '14px',
    fontWeight: 600,
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  languageCount: {
    backgroundColor: colors.base.off_white,
    borderRadius: '12px',
    color: colors.base.slate_gray,
    flexShrink: 0,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 'normal',
    px: '8px',
    py: '4px',
  },
  previewBox: {
    alignItems: 'center',
    backgroundColor: colors.base.off_white,
    border: `1px solid ${colors.base.blue_gray}`,
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    px: 2,
    py: '12px',
  },
  ctaSwitchLabel: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: '12px',
    m: 0,
  },
  ctaSwitch: {
    width: 36,
    height: 20,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: '2px',
      '&:hover': { backgroundColor: 'transparent' },
      '&.Mui-checked': {
        color: colors.base.light,
        transform: 'translateX(16px)',
        '&:hover': { backgroundColor: 'transparent' },
      },
    },
    '& .MuiSwitch-thumb': {
      width: 16,
      height: 16,
      backgroundColor: colors.base.light,
      boxShadow: 'none',
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#d5dde3',
      borderRadius: 10,
      opacity: 1,
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: colors.base.walmart_blue,
      opacity: 1,
    },
  },
  ctaLabel: (isEnabled) => ({
    color: isEnabled ? colors.base.bentonville_blue : colors.base.slate_gray,
    fontSize: '14px',
    fontWeight: 700,
  }),
  ctaState: {
    color: colors.base.slate_gray,
    fontSize: '12px',
    fontWeight: 700,
  },
  ctaFields: {
    flexDirection: { xs: 'column', sm: 'row' },
    gap: '20px',
  },
  ctaTextField: {
    flex: 1,
    maxWidth: { sm: 300 },
    minWidth: 0,
  },
  ctaLinkField: {
    flex: 1,
    minWidth: 0,
  },
  linkIcon: {
    color: colors.base.slate_gray,
    fontSize: 16,
  },
  choices: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '40px',
  },
  choiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '12px',
  },
  appearanceButton: (isSelected) => ({
    border: '2px solid',
    borderColor: isSelected ? colors.base.walmart_blue : 'transparent',
    borderRadius: '16px',
    height: 52,
    opacity: isSelected ? 1 : 0.6,
    p: '2px',
    width: 124,
  }),
  appearanceInner: (appearance) => ({
    alignItems: 'center',
    backgroundColor: appearance.background,
    border:
      appearance.value === 'low'
        ? `1px solid ${colors.base.blue_gray}`
        : 'none',
    borderRadius: '12px',
    color: appearance.accent,
    display: 'flex',
    fontSize: '14px',
    fontWeight: 700,
    height: '100%',
    justifyContent: 'center',
    whiteSpace: 'pre',
    width: '100%',
  }),
  iconButton: (isSelected) => ({
    border: '2px solid',
    borderColor: isSelected ? colors.base.walmart_blue : 'transparent',
    borderRadius: '16px',
    color: colors.base.bentonville_blue,
    height: 48,
    opacity: isSelected ? 1 : 0.6,
    width: 48,
  }),
  dates: {
    flexDirection: { xs: 'column', sm: 'row' },
    gap: '20px',
    maxWidth: 600,
  },
  dateField: {
    flex: 1,
    minWidth: 0,
  },

  dateInput: {
    ...adminFormStyles.field,
    '& .MuiOutlinedInput-root': {
      ...adminFormStyles.field['& .MuiOutlinedInput-root'],
      color: colors.base.slate_gray,
    },
    '& .MuiOutlinedInput-input': {
      color: colors.base.slate_gray,
      fontSize: '14px',
      padding: '12px 16px',
      '&::placeholder': {
        color: colors.base.slate_gray,
        opacity: 1,
      },
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
      color: colors.base.slate_gray,
    },
  },
  resetButton: {
    alignSelf: 'flex-start',
    color: colors.base.walmart_blue,
    fontSize: '12px',
    fontWeight: 700,
    minWidth: 0,
    p: 0,
    textTransform: 'none',
    '&:hover': { backgroundColor: 'transparent' },
  },
};

// Send the admin straight to the first field that needs attention, top to
// bottom, so a failed "Schedule alert" doesn't just paint errors off screen.
function focusFirstError(errors, selectLanguage) {
  const languageWithError = ALL_LANGUAGES.find(
    (langKey) => errors.languages?.[langKey]
  );
  if (languageWithError) selectLanguage(languageWithError);

  const languageErrors = errors.languages?.[languageWithError] || {};
  const targetId =
    (languageErrors.headline && 'alert-headline') ||
    (languageErrors.details && 'alert-details') ||
    (errors.appearance && 'alert-appearance') ||
    (languageErrors.cta_text && 'alert-cta-text') ||
    (languageErrors.cta_link && 'alert-cta-link') ||
    (errors.start && 'alert-start') ||
    (errors.end && 'alert-end');

  if (!targetId) return;

  requestAnimationFrame(() => {
    const element = document.getElementById(targetId);
    if (!element) return;

    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    element.focus({ preventScroll: true });
  });
}

export default function AlertForm({ onBack, onSchedule }) {
  const [alert, setAlert] = useState(() => createEmptyAlert());
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [validationErrors, setValidationErrors] = useState({});
  const [isTargetingValid, setIsTargetingValid] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const timeZone = moment.tz(moment.tz.guess()).format('z');
  const languageCopy = alert.languages[language];
  const activeLanguageErrors = validationErrors.languages?.[language] || {};

  function updateAlert(field, value) {
    setAlert((currentAlert) => ({ ...currentAlert, [field]: value }));
    setValidationErrors((currentErrors) =>
      currentErrors[field]
        ? { ...currentErrors, [field]: undefined }
        : currentErrors
    );
  }

  function updateLanguage(field, value) {
    setAlert((currentAlert) => ({
      ...currentAlert,
      languages: {
        ...currentAlert.languages,
        [language]: {
          ...currentAlert.languages[language],
          [field]: value,
        },
      },
    }));
    setValidationErrors((currentErrors) => {
      const languageErrors = currentErrors.languages?.[language];
      if (!languageErrors?.[field]) return currentErrors;

      const nextLanguages = {
        ...currentErrors.languages,
        [language]: { ...languageErrors, [field]: undefined },
      };
      const stillInvalid = Object.values(nextLanguages).some((errs) =>
        Object.values(errs).some(Boolean)
      );

      return {
        ...currentErrors,
        languages: nextLanguages,
        summary: stillInvalid ? currentErrors.summary : undefined,
      };
    });
  }

  function filledFieldCount(langKey) {
    const copy = alert.languages[langKey];
    return [copy.headline, copy.details].filter((value) => value.trim()).length;
  }

  function languageHasError(langKey) {
    const languageErrors = validationErrors.languages?.[langKey];
    return Boolean(
      languageErrors && Object.values(languageErrors).some(Boolean)
    );
  }

  function handleTargetingChanged(nextTargeting, isValid) {
    updateAlert('targeting', nextTargeting);
    setIsTargetingValid(isValid);
  }

  function scheduleAlert() {
    setIsValidating(true);
    const result = validateAlert(alert);
    setValidationErrors(result.errors);

    if (result.isValid && isTargetingValid) {
      onSchedule(alert);
    } else if (!result.isValid) {
      focusFirstError(result.errors, setLanguage);
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing="20px">
        <Box>
          <Button
            color="inherit"
            disableRipple
            startIcon={<ArrowBack sx={styles.backIcon} />}
            size="small"
            sx={styles.backButton}
            onClick={onBack}
          >
            Alerts
          </Button>
          <Typography component="h1" sx={styles.pageTitle}>
            Create alert
          </Typography>
        </Box>

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <Box>
              <Typography component="h2" sx={styles.sectionTitle}>
                Alert contents
              </Typography>
              <Typography sx={styles.sectionDescription}>
                This will show prominently at the top of their home screen.
                {languageVersionCopy}
              </Typography>
            </Box>

            <Box>
              <Box
                sx={styles.languageSelector}
                role="group"
                aria-label="Language"
              >
                {ALL_LANGUAGES.map((langKey) => {
                  const isSelected = language === langKey;
                  const option = languageOption(langKey);
                  return (
                    <ButtonBase
                      key={langKey}
                      aria-pressed={isSelected}
                      onClick={() => setLanguage(langKey)}
                      sx={styles.languageOption(isSelected)}
                    >
                      <Box
                        component="span"
                        sx={styles.languageBadge(isSelected)}
                      >
                        {option.code}
                      </Box>
                      <Box component="span" sx={styles.languageName}>
                        {option.label}
                      </Box>
                      {languageHasError(langKey) ? (
                        <Warning color="error" />
                      ) : (
                        <Box component="span" sx={styles.languageCount}>
                          {filledFieldCount(langKey)}/2
                        </Box>
                      )}
                    </ButtonBase>
                  );
                })}
              </Box>
              {validationErrors.summary && (
                <FormHelperText error>
                  {validationErrors.summary}
                </FormHelperText>
              )}
            </Box>

            <Typography sx={styles.fieldLabel}>
              Click on the text you want to change in the alert preview below.
            </Typography>

            <Box>
              <Box sx={styles.previewBox}>
                <AlertPreview
                  alert={alert}
                  language={language}
                  onChange={updateLanguage}
                />
              </Box>
              {activeLanguageErrors.headline && (
                <FormHelperText error>
                  {activeLanguageErrors.headline}
                </FormHelperText>
              )}
              {activeLanguageErrors.details && (
                <FormHelperText error>
                  {activeLanguageErrors.details}
                </FormHelperText>
              )}
            </Box>

            <FormControlLabel
              sx={styles.ctaSwitchLabel}
              control={
                <Switch
                  checked={alert.cta_enabled}
                  color="default"
                  onChange={(event) =>
                    updateAlert('cta_enabled', event.target.checked)
                  }
                  sx={styles.ctaSwitch}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={styles.ctaLabel(alert.cta_enabled)}>
                    Call-To-Action button
                  </Typography>
                  <Typography sx={styles.ctaState}>
                    {alert.cta_enabled ? 'ON' : 'OFF'}
                  </Typography>
                </Stack>
              }
            />

            {alert.cta_enabled && (
              <Stack sx={styles.ctaFields}>
                <Stack spacing={1} sx={styles.ctaTextField}>
                  <FormLabel htmlFor="alert-cta-text" sx={styles.fieldLabel}>
                    Button Text
                  </FormLabel>
                  <TextField
                    fullWidth
                    id="alert-cta-text"
                    placeholder="Go"
                    value={languageCopy.cta_text}
                    onChange={(event) =>
                      updateLanguage('cta_text', event.target.value)
                    }
                    error={Boolean(activeLanguageErrors.cta_text)}
                    helperText={activeLanguageErrors.cta_text}
                    inputProps={{ maxLength: CTA_TEXT_MAX_LENGTH }}
                    sx={styles.field}
                  />
                </Stack>
                <Stack spacing={1} sx={styles.ctaLinkField}>
                  <FormLabel htmlFor="alert-cta-link" sx={styles.fieldLabel}>
                    Link to
                  </FormLabel>
                  <TextField
                    fullWidth
                    id="alert-cta-link"
                    placeholder="Paste a link..."
                    value={languageCopy.cta_link}
                    onChange={(event) =>
                      updateLanguage('cta_link', event.target.value)
                    }
                    error={Boolean(activeLanguageErrors.cta_link)}
                    helperText={activeLanguageErrors.cta_link}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkOutlined sx={styles.linkIcon} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip arrow title={LINK_HELP}>
                            <InfoOutlined sx={styles.linkIcon} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={styles.field}
                  />
                </Stack>
              </Stack>
            )}

            <Stack sx={styles.choices}>
              <Stack spacing={1.5}>
                <Typography sx={styles.fieldLabel}>Urgency</Typography>
                <Stack id="alert-appearance" sx={styles.choiceRow}>
                  {ALERT_APPEARANCES.map((appearance) => (
                    <ButtonBase
                      key={appearance.value}
                      aria-pressed={alert.appearance === appearance.value}
                      onClick={() =>
                        updateAlert('appearance', appearance.value)
                      }
                      sx={styles.appearanceButton(
                        alert.appearance === appearance.value
                      )}
                    >
                      <Box sx={styles.appearanceInner(appearance)}>
                        {appearance.label}
                      </Box>
                    </ButtonBase>
                  ))}
                </Stack>
                {validationErrors.appearance && (
                  <FormHelperText error>
                    {validationErrors.appearance}
                  </FormHelperText>
                )}
              </Stack>
              <Stack spacing={1.5}>
                <Typography sx={styles.fieldLabel}>Icon</Typography>
                <Stack sx={styles.choiceRow}>
                  {ALERT_ICONS.map(({ name, Icon }) => (
                    <ButtonBase
                      key={name}
                      aria-label={name.replace('Outlined', '')}
                      aria-pressed={alert.icon === name}
                      onClick={() => updateAlert('icon', name)}
                      sx={styles.iconButton(alert.icon === name)}
                    >
                      <Icon />
                    </ButtonBase>
                  ))}
                </Stack>
              </Stack>
            </Stack>

            <Divider />

            <Stack sx={styles.dates}>
              <Stack spacing={1} sx={styles.dateField}>
                <FormLabel htmlFor="alert-start" sx={styles.fieldLabel}>
                  Start time
                </FormLabel>
                <DateTimePicker
                  format="MM/DD/YYYY hh:mm A"
                  value={alert.start}
                  onChange={(value) => updateAlert('start', value)}
                  slotProps={{
                    textField: {
                      id: 'alert-start',
                      fullWidth: true,
                      placeholder: 'Immediately',
                      error: Boolean(validationErrors.start),
                      helperText: validationErrors.start,
                    },
                  }}
                  sx={styles.dateInput}
                />
                {alert.start && (
                  <Button
                    disableRipple
                    onClick={() => updateAlert('start', null)}
                    sx={styles.resetButton}
                  >
                    Reset to Immediately
                  </Button>
                )}
              </Stack>
              <Stack spacing={1} sx={styles.dateField}>
                <FormLabel htmlFor="alert-end" sx={styles.fieldLabel}>
                  End time
                </FormLabel>
                <DateTimePicker
                  format="MM/DD/YYYY hh:mm A"
                  value={alert.end}
                  onChange={(value) => updateAlert('end', value)}
                  slotProps={{
                    textField: {
                      id: 'alert-end',
                      fullWidth: true,
                      placeholder: 'MM/DD/YYYY 11:59 PM',
                      error: Boolean(validationErrors.end),
                      helperText: validationErrors.end,
                    },
                  }}
                  sx={styles.dateInput}
                />
              </Stack>
            </Stack>
            <Typography sx={styles.helperText}>
              Times are in your time zone ({timeZone}).
            </Typography>
          </Stack>
        </Paper>

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <TargetingForm
              hideUsers
              isValidating={isValidating}
              onStateChanged={handleTargetingChanged}
              subject="alert"
            />
          </Stack>
        </Paper>

        <Paper component="footer" variant="outlined" sx={styles.section}>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            padding="16px 20px"
          >
            <Button
              type="button"
              variant="contained"
              onClick={scheduleAlert}
              sx={styles.primaryButton}
            >
              Schedule alert
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </LocalizationProvider>
  );
}

AlertForm.propTypes = {
  onBack: PropTypes.func,
  onSchedule: PropTypes.func,
};

AlertForm.defaultProps = {
  onBack: () => {},
  onSchedule: () => {},
};
